import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { TAT_COLORS } from "@/lib/fields";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { ArrowRight, ListFilter } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const CHART_TICK = { fontSize: 11 };

function TatBadge({ tat }) {
  if (!tat) return <span className="text-[#9CA3AF]">—</span>;
  return (
    <span className="text-[11px] font-bold text-white px-2 py-0.5 rounded-sm" style={{ backgroundColor: TAT_COLORS[tat] }}>
      {tat}
    </span>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [clientFilter, setClientFilter] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- api is a stable import

  if (!stats) return <div className="text-sm text-[#4B5563]" data-testid="dashboard-loading">Loading dashboard...</div>;

  const kpis = [
    { label: "Total Cases", value: stats.total, color: "#111827", testid: "kpi-total", to: "/cases" },
    { label: "Open Cases", value: stats.open, color: "#2563EB", testid: "kpi-open", to: "/cases" },
    { label: "In TAT", value: stats.tat["In TAT"], color: TAT_COLORS["In TAT"], testid: "kpi-in-tat", to: "/cases?tat=In TAT", isTat: true },
    { label: "Due Today", value: stats.tat["Due Today"], color: TAT_COLORS["Due Today"], testid: "kpi-due-today", to: "/cases?tat=Due Today", isTat: true },
    { label: "Out TAT", value: stats.tat["Out TAT"], color: TAT_COLORS["Out TAT"], testid: "kpi-out-tat", to: "/cases?tat=Out TAT", isTat: true },
    { label: "Over TAT", value: stats.tat["Over TAT"], color: TAT_COLORS["Over TAT"], testid: "kpi-over-tat", to: "/cases?tat=Over TAT", isTat: true },
    { label: "Beyond TAT", value: stats.tat["Beyond TAT"], color: TAT_COLORS["Beyond TAT"], testid: "kpi-beyond-tat", to: "/cases?tat=Beyond TAT", isTat: true },
  ];

  const chartData = Object.entries(TAT_COLORS).map(([name, color]) => ({ name, value: stats.tat[name] || 0, color }));
  const bucketTotal = chartData.reduce((s, d) => s + d.value, 0);
  const pct = (n, total) => (total ? ` (${Math.round((100 * n) / total)}%)` : "");

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-[#111827] tracking-tight">TAT Overview</h1>
        <p className="text-sm text-[#4B5563] mt-1">Aging of open verification cases, computed live from Received Date.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4">
        {kpis.map((k) => (
          <button key={k.label} onClick={() => navigate(k.to)} data-testid={k.testid}
            className="bg-white border border-[#E5E7EB] p-4 text-left hover:border-[#2563EB] transition-colors duration-150 group">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#4B5563]">{k.label}</div>
            <div className="font-heading text-3xl font-extrabold mt-2" style={{ color: k.color }}>{k.value}</div>
            {k.isTat && (
              <div className="text-[10px] font-semibold text-[#9CA3AF] mt-1" data-testid={`${k.testid}-pct`}>
                {stats.open ? Math.round((100 * k.value) / stats.open) : 0}% of open
              </div>
            )}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E5E7EB] p-4 lg:col-span-2">
          <div className="text-xs font-bold uppercase tracking-widest text-[#4B5563] mb-4">Open Cases by TAT Bucket</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData}>
              <XAxis dataKey="name" tick={CHART_TICK} stroke="#9CA3AF" />
              <YAxis allowDecimals={false} tick={CHART_TICK} stroke="#9CA3AF" />
              <Tooltip formatter={(v) => [`${v} (${bucketTotal ? Math.round((100 * v) / bucketTotal) : 0}%)`, "Cases"]} />
              <Bar dataKey="value" radius={0} label={{ position: "top", fontSize: 10, fill: "#4B5563", formatter: (v) => `${v} (${bucketTotal ? Math.round((100 * v) / bucketTotal) : 0}%)` }}>
                {chartData.map((d) => <Cell key={d.name} fill={d.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-[#E5E7EB] p-4">
          <div className="text-xs font-bold uppercase tracking-widest text-[#4B5563] mb-4">Pending by Field Executive</div>
          <div className="space-y-2" data-testid="fe-pending-list">
            {stats.fe_pending.length === 0 && <div className="text-sm text-[#9CA3AF]">No pending cases.</div>}
            {stats.fe_pending.map((f) => (
              <div key={f.fe_name} className="flex items-center justify-between text-sm border-b border-[#F3F4F6] pb-2">
                <span className="text-[#111827] font-medium truncate">{f.fe_name}</span>
                <span className="font-mono font-bold text-[#2563EB]">{f.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB]">
        <div className="p-4 border-b border-[#E5E7EB] text-xs font-bold uppercase tracking-widest text-[#4B5563]">Client-wise Summary</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap" data-testid="client-summary-table">
            <thead>
              <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
                <th className="text-left py-2 px-3">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest transition-colors hover:text-[#2563EB] ${clientFilter ? "text-[#2563EB]" : "text-[#4B5563]"}`} data-testid="summary-filter-client">
                        Client <ListFilter className={`h-3 w-3 ${clientFilter ? "" : "opacity-40"}`} />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-52 p-2 rounded-sm">
                      <Input value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} placeholder="Filter contains..."
                        className="rounded-sm h-8 text-xs mb-2" data-testid="summary-filter-client-input" />
                      <Button size="sm" variant="ghost" className="rounded-sm h-7 text-xs" onClick={() => setClientFilter("")} data-testid="summary-filter-client-clear">Clear</Button>
                    </PopoverContent>
                  </Popover>
                </th>
                {["Total", "INSUFF", "Open (excl. INSUFF)", "In TAT", "Due Today", "Out TAT", "Over TAT", "Beyond TAT"].map((h) => (
                  <th key={h} className="text-left text-[10px] font-bold uppercase tracking-widest text-[#4B5563] py-2 px-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(stats.client_stats || []).length === 0 && (
                <tr><td colSpan={9} className="py-6 text-center text-[#9CA3AF] text-sm">No cases yet.</td></tr>
              )}
              {(stats.client_stats || []).filter((c) => c.client.toLowerCase().includes(clientFilter.toLowerCase())).map((c) => (
                <tr key={c.client} className="border-b border-[#F3F4F6] hover:bg-[#F3F4F6] transition-colors" data-testid={`client-summary-row-${c.client}`}>
                  <td className="py-2 px-3 font-semibold">{c.client}</td>
                  <td className="py-2 px-3 font-mono font-bold">{c.total}</td>
                  <td className="py-2 px-3 font-mono font-bold text-[#9333EA]">{c.insuff}{pct(c.insuff, c.total)}</td>
                  <td className="py-2 px-3 font-mono font-bold">{c.open}{pct(c.open, c.total)}</td>
                  {["In TAT", "Due Today", "Out TAT", "Over TAT", "Beyond TAT"].map((t) => (
                    <td key={t} className="py-2 px-3 font-mono font-bold" style={{ color: TAT_COLORS[t] }}>{c.tat[t]}{pct(c.tat[t], c.total)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB]">
        <div className="flex items-center justify-between p-4 border-b border-[#E5E7EB]">
          <div className="text-xs font-bold uppercase tracking-widest text-[#4B5563]">Recently Added Cases</div>
          <button onClick={() => navigate("/cases")} className="text-xs text-[#2563EB] font-semibold flex items-center gap-1 hover:underline" data-testid="view-all-cases-link">
            View all <ArrowRight className="h-3 w-3" />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="recent-cases-table">
            <thead>
              <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
                {["Sr.", "AC Ref. No.", "Client", "Company", "Ref No.", "Candidate", "Pincode", "District", "City (TQ)", "FE", "Old Days", "TAT", "Status"].map((h) => (
                  <th key={h} className="text-left text-[10px] font-bold uppercase tracking-widest text-[#4B5563] py-2 px-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stats.recent.length === 0 && (
                <tr><td colSpan={13} className="py-8 text-center text-[#9CA3AF] text-sm">No cases yet. Create one or import from Excel.</td></tr>
              )}
              {stats.recent.map((c) => (
                <tr key={c.id} className="border-b border-[#F3F4F6] hover:bg-[#F3F4F6] transition-colors cursor-pointer"
                  onClick={() => navigate(`/cases?open=${c.id}`)} data-testid={`recent-case-row-${c.id}`}>
                  <td className="py-2 px-3 font-mono text-xs">{c.sr_no ?? "—"}</td>
                  <td className="py-2 px-3 font-mono text-xs">{c.ac_ref_no || "—"}</td>
                  <td className="py-2 px-3">{c.client_name || "—"}</td>
                  <td className="py-2 px-3 font-medium">{c.company_name || "—"}</td>
                  <td className="py-2 px-3 font-mono text-xs">{c.ref_no || "—"}</td>
                  <td className="py-2 px-3">{c.candidate_name || "—"}</td>
                  <td className="py-2 px-3 font-mono text-xs">{c.pincode || "—"}</td>
                  <td className="py-2 px-3">{c.district || "—"}</td>
                  <td className="py-2 px-3">{c.city_tq || "—"}</td>
                  <td className="py-2 px-3">{c.fe_name || "—"}</td>
                  <td className="py-2 px-3 font-mono text-xs">{c.old_days ?? "—"}</td>
                  <td className="py-2 px-3"><TatBadge tat={c.tat} /></td>
                  <td className="py-2 px-3">{c.case_status || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
